import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText, formatPrice, formatDuration } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ChevronRight, Clock, MapPin, User, Mail, Phone, Globe, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders } = useAuthStore();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/services/${id}`);
        setService(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleContact = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        recipient_id: service.teacher?.id,
        recipient_type: 'teacher',
        subject: `Consulta sobre ${getLocalizedText(service.title, i18n.language)}`,
        content: message,
        related_service_id: service.id,
      }, { headers: getAuthHeaders() });
      setContactOpen(false);
      setMessage('');
      alert('Mensaje enviado correctamente');
    } catch (e) {
      alert('Error al enviar mensaje');
    }
    setSending(false);
  };

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!service) return <div className="text-center py-12">Servicio no encontrado</div>;

  const hasServiceImage = service.images && service.images.length > 0;
  const hasTeacherImage = service.teacher?.images && service.teacher.images.length > 0;
  const teacher = service.teacher || {};
  
  const audienceLabels = {
    all: 'Todos', women: 'Mujeres', men: 'Hombres', couples: 'Parejas', lgbtqia: 'LGBTQIA+'
  };
  
  const serviceTypeLabels = {
    tantric_massage: 'Masaje Tántrico', tantra_coaching: 'Coaching Tántrico',
    couple_session: 'Sesión de Pareja', tantra_therapy: 'Terapia Tántrica',
    tantra_initiation: 'Iniciación Tántrica', yoga: 'Yoga',
    meditation: 'Meditación', breathwork: 'Respiración', bodywork: 'Trabajo Corporal'
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="service-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="h-64 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasServiceImage ? (
            <img src={service.images[0]} alt={getLocalizedText(service.title, i18n.language)} className="w-full h-full object-cover" />
          ) : (
            <Sparkles className="h-24 w-24 text-gold opacity-30" style={{ color: '#D4A574' }} />
          )}
        </div>
        
        <div className="p-8">
          <div className="flex flex-wrap gap-2 mb-3">
            {service.is_featured && <Badge className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
            {service.service_type && <Badge variant="outline">{serviceTypeLabels[service.service_type] || service.service_type}</Badge>}
          </div>
          
          <div className="flex items-start justify-between mb-6">
            <h1 className="font-serif text-3xl font-bold text-primary">{getLocalizedText(service.title, i18n.language)}</h1>
            {service.price > 0 && (
              <span className="text-2xl font-bold text-gold" style={{ color: '#D4A574' }}>{formatPrice(service.price, service.currency)}</span>
            )}
          </div>

          <Link to={`/teacher/${teacher.id}`} className="block mb-6">
            <div className="flex items-center gap-4 p-4 bg-cream rounded-xl hover:bg-cream-dark transition-colors" style={{ backgroundColor: '#F8F6F3' }}>
              <div className="w-16 h-16 rounded-full bg-white overflow-hidden flex-shrink-0 shadow-sm">
                {hasTeacherImage ? (
                  <img src={teacher.images[0]} alt={service.teacher_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-8 w-8 text-gold" style={{ color: '#D4A574' }} />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-primary">{service.teacher_name}</p>
                <p className="text-sm text-secondary">Profesional</p>
              </div>
              <ChevronRight className="h-5 w-5 text-secondary" />
            </div>
          </Link>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {service.duration_minutes > 0 && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-5 w-5 text-gold" style={{ color: '#D4A574' }} />
                <div>
                  <p className="text-xs text-secondary">Duración</p>
                  <p className="font-medium text-sm">{formatDuration(service.duration_minutes)}</p>
                </div>
              </div>
            )}
            {service.location?.city && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-gold" style={{ color: '#D4A574' }} />
                <div>
                  <p className="text-xs text-secondary">Ubicación</p>
                  <p className="font-medium text-sm">{service.location.city}</p>
                </div>
              </div>
            )}
            {service.location?.postal_code && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-gold" style={{ color: '#D4A574' }} />
                <div>
                  <p className="text-xs text-secondary">Código Postal</p>
                  <p className="font-medium text-sm">{service.location.postal_code}</p>
                </div>
              </div>
            )}
          </div>
          
          {service.target_audience && service.target_audience.length > 0 && !service.target_audience.includes('all') && (
            <div className="mb-6">
              <h3 className="font-semibold text-primary mb-2">Reservado a</h3>
              <div className="flex flex-wrap gap-2">
                {service.target_audience.map(audience => (
                  <Badge key={audience} variant="outline" className="bg-cream" style={{ backgroundColor: '#F8F6F3' }}>
                    {audienceLabels[audience] || audience}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="prose max-w-none mt-6 mb-8">
            <h3 className="font-serif text-xl font-semibold mb-3">Descripción</h3>
            <p className="text-secondary whitespace-pre-line">{getLocalizedText(service.description, i18n.language)}</p>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-serif text-xl font-semibold mb-4">Contacto</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {teacher.phone && (
                <a href={`https://wa.me/${teacher.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors" data-testid="contact-whatsapp">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-green-800">WhatsApp</p>
                    <p className="text-sm text-green-600">{teacher.phone}</p>
                  </div>
                </a>
              )}

              {teacher.phone && (
                <a href={`tel:${teacher.phone}`} className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors" data-testid="contact-phone">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-800">Telefono</p>
                    <p className="text-sm text-blue-600">{teacher.phone}</p>
                  </div>
                </a>
              )}

              {service.teacher_email && (
                <a href={`mailto:${service.teacher_email}`} className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors" data-testid="contact-email">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-purple-800">Email</p>
                    <p className="text-sm text-purple-600 truncate">{service.teacher_email}</p>
                  </div>
                </a>
              )}

              {teacher.website && (
                <a href={teacher.website} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors" data-testid="contact-website">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-orange-800">Sito Web</p>
                    <p className="text-sm text-orange-600 truncate">{teacher.website.replace(/^https?:\/\//, '')}</p>
                  </div>
                </a>
              )}
            </div>

            {isAuthenticated && (
              <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full mt-4 bg-gold hover:bg-gold/90 text-white py-3" style={{ backgroundColor: '#D4A574' }} data-testid="send-message-btn">
                    <Mail className="h-5 w-5 mr-2" /> Enviar Mensaje
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Contactar a {service.teacher_name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Textarea placeholder="Escribe tu mensaje..." value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="contact-message" />
                    <Button onClick={handleContact} disabled={!message || sending} className="w-full bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="submit-message-btn">
                      {sending ? 'Enviando...' : 'Enviar Mensaje'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {!isAuthenticated && (
              <Link to="/login" className="block mt-4">
                <Button className="w-full bg-gold hover:bg-gold/90 text-white py-3" style={{ backgroundColor: '#D4A574' }}>
                  Iniciar sesion para enviar mensaje
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
